import { useState, useEffect, useCallback, useRef } from 'react'
import type { TileConfig } from '../types/tiles'
import { DEFAULT_TILES } from '../config/defaultLayout'
import { fetchLayout, updateLayout } from '@/shared/lib/api'

export function useDashboardLayout() {
  const [tiles, setTiles] = useState<TileConfig[]>(DEFAULT_TILES)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const saveTimeoutRef = useRef<number>()

  // Load layout from backend
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const data = await fetchLayout()
        if (data.tiles && data.tiles.length > 0) {
          const existingIds = new Set<string>(data.tiles.map(t => t.id))
          const missingDefaults = DEFAULT_TILES.filter(t => !existingIds.has(t.id))
          setTiles([...data.tiles, ...missingDefaults])
        }
      } catch {
        // Layout fetch failed — fall back to defaults already set in state
      } finally {
        setIsLoading(false)
      }
    }

    loadLayout()
  }, [])

  // Save layout to backend with debounce
  const saveLayout = useCallback(async (newTiles: TileConfig[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    setIsSaving(true)

    saveTimeoutRef.current = window.setTimeout(async () => {
      try {
        await updateLayout({ tiles: newTiles })
      } catch {
        // Save failed silently — next reorder will retry
      } finally {
        setIsSaving(false)
      }
    }, 500)
  }, [])

  // Reorder tiles
  const reorderTiles = useCallback((newTiles: TileConfig[]) => {
    setTiles(newTiles)
    saveLayout(newTiles)
  }, [saveLayout])

  // Toggle tile visibility (preserves position in layout)
  const toggleTileVisibility = useCallback((id: string) => {
    const newTiles = tiles.map(t =>
      t.id === id ? { ...t, visible: t.visible === false ? undefined : false } : t
    )
    setTiles(newTiles)
    saveLayout(newTiles)
  }, [tiles, saveLayout])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  return {
    tiles,
    isLoading,
    isSaving,
    reorderTiles,
    toggleTileVisibility,
  }
}
